import { CloseIcon, FullScreenIcon } from '@treams/treams-icons';
import { PropsWithChildren, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Button } from './Button';
import { IconButton } from './IconButton';
import { Heading } from './Text';

const ContentsContainer = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlueLight};
	overflow-y: auto;
`;

function Contents(props: PropsWithChildren<BoxProps>) {
	return <ContentsContainer p={3} flexGrow={1} flexShrink={1} {...props} />;
}

const HeaderContainer = styled(Box)<{ tabs: boolean }>`
	${(props) => !props.tabs && `border-bottom: 1px solid ${props.theme.colors.lightBlue};`}
`;

const FooterContainer = styled(Flex)`
	background: ${(props) => props.theme.colors.white};
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	justify-content: flex-end;
`;

function Escape(props: { onEscape: () => void }) {
	useLayoutEffect(() => {
		function onKeyDown(event: KeyboardEvent) {
			const element = document.activeElement as HTMLElement;

			if (event.key === 'Escape' && element?.tabIndex === -1) {
				props.onEscape();
			}
		}

		document.addEventListener('keydown', onKeyDown);

		return () => document.removeEventListener('keydown', onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}

function Header(
	props: PropsWithChildren<
		BoxProps & { title: string; tabs?: boolean; onFullScreen?: () => void; onClose?: () => void }
	>,
) {
	return (
		<HeaderContainer flexGrow={0} flexShrink={0} tabs={!!props.tabs}>
			<Flex p={3} pb={props.tabs ? 0 : 3} alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					{props.title}
				</Heading>
				{props.onFullScreen && (
					<IconButton icon={<FullScreenIcon />} type="plain" onClick={props.onFullScreen} mr={2} />
				)}
				{props.onClose && (
					<>
						<Escape onEscape={props.onClose} />
						<IconButton icon={<CloseIcon />} type="plain" onClick={props.onClose} />
					</>
				)}
			</Flex>
		</HeaderContainer>
	);
}

function Footer(props: PropsWithChildren<BoxProps>) {
	return <FooterContainer p={3} width="100%" flexGrow={0} flexShrink={0} {...props} />;
}

function FormFooter(props: {
	small?: boolean;
	onCancel?: () => void;
	onSave?: () => void;
	disabled?: boolean;
	saveTitle?: string;
}) {
	const { t } = useTranslation();

	return (
		<Footer>
			{props.onCancel && (
				<Button
					type="secondary"
					title={t('modal.footer.form.cancel')}
					small={props.small}
					flexGrow={0}
					flexShrink={0}
					onClick={props.onCancel}
				/>
			)}
			<Button
				type="primary"
				title={props.saveTitle ?? t('modal.footer.form.save')}
				small={props.small}
				flexGrow={0}
				flexShrink={0}
				ml={2}
				onClick={props.onSave}
				disabled={props.disabled}
			/>
		</Footer>
	);
}

function FormFooterFormik(props: {
	small?: boolean;
	onCancel?: () => void;
	disabled?: boolean;
	saveTitle?: string;
	children?: React.ReactNode;
}) {
	const { t } = useTranslation();

	return (
		<Footer>
			<Flex flexGrow={1} justifyContent={'flex-start'}>
				{props.children}
			</Flex>
			<Button
				type="secondary"
				title={t('modal.footer.form.cancel')}
				small={props.small}
				flexGrow={0}
				flexShrink={0}
				onClick={props.onCancel}
			/>
			<Button.Formik
				type="primary"
				title={props.saveTitle ?? t('modal.footer.form.save')}
				small={props.small}
				flexGrow={0}
				flexShrink={0}
				ml={2}
				disabled={props.disabled}
			/>
		</Footer>
	);
}

FormFooter.Formik = FormFooterFormik;

export function Modal(
	props: PropsWithChildren<BoxProps & { width?: string | number; height?: string | number }>,
) {
	return (
		<Flex width={props.width ?? 600} height={props.height} flexDirection="column">
			{props.children}
		</Flex>
	);
}

Modal.Header = Header;
Modal.Contents = Contents;
Modal.Footer = Footer;
Modal.FormFooter = FormFooter;
